import {
  RequestTreasuryBill,
  TreasuryBillAssetSummary,
  TreasuryBillDetail,
  SellTreasuryBill,
} from '@typeDefs/domain/assets';
import { treasuryBillMapper } from '@services/mappers';
import apiClient from './client';
import { parseSaleSubmitError } from './saleSubmitError';
// Reserve-related API calls
export const treasuryBillService = {
  getAllTreasuryBills: async (businessId: string): Promise<TreasuryBillAssetSummary[]> => {
    // Fixed return type
    try {
      // Use the generated API method
      const response = await apiClient.api.assetsTreasuryBillsList({
        CorporateEntityId: businessId,
      });
      const mappedItems = treasuryBillMapper.paginatedFromApi(response.data);

      return mappedItems; //
    } catch (error) {
      console.error(
        'Error fetching treasury bills:',
        error instanceof Error ? error.message : error,
      );
      throw new Error('Failed to fetch treasury bills');
    }
  },

  // `idempotencyKey` is a STABLE per-action key generated once per user action
  // (one buy click) and reused across retries/double-submits so the server
  // dedupes them. When supplied it is sent as the `Idempotency-Key` header.
  submitTreasuryBillOrder: async (
    order: RequestTreasuryBill,
    idempotencyKey?: string,
  ): Promise<string> => {
    try {
      // Use the generated API method with correct property names
      const response = await apiClient.api.assetsTreasuryBillsCreate(
        {
          treasuryBillListingId: order.treasuryBillListingId,
          faceValue: order.faceValue,
          corporateEntityId: order.corporateEntityId,
          requestedPurchasePrice: order.requestedPurchasePrice,
          ...(order.autoRoll
            ? { autoRoll: true, autoRollTenorWeeks: order.autoRollTenorWeeks }
            : {}),
          ...(order.autoRoll && order.replacesAutoRollInstructionId
            ? { replacesAutoRollInstructionId: order.replacesAutoRollInstructionId }
            : {}),
        },
        idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined,
      );
      return response.data; // Returns the order ID
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Error submitting order:', error instanceof Error ? error.message : error);
      // 409 = duplicate / in-flight same key — static message, no auto-resubmit (#195).
      if (error?.response?.status === 409) {
        throw new Error('Request is already in progress');
      }
      // Otherwise parse the structured server-error body into a typed SaleSubmitError so the
      // purchase component can surface a real message (#183 — was previously swallowed).
      throw parseSaleSubmitError(error, 'Failed to submit treasury bill purchase order');
    }
  },

  // Plural variant hits the SAME buy endpoint — give it the same idempotency
  // treatment so the latent duplicate path is covered.
  submitTreasuryBillOrders: async (
    order: RequestTreasuryBill[],
    idempotencyKey?: string,
  ): Promise<string> => {
    try {
      // Use the generated API method with correct property names
      const response = await apiClient.api.assetsTreasuryBillsCreate(
        {
          treasuryBillListingId: order[0].treasuryBillListingId,
          faceValue: order[0].faceValue,
          ...(order[0].autoRoll
            ? { autoRoll: true, autoRollTenorWeeks: order[0].autoRollTenorWeeks }
            : {}),
          ...(order[0].autoRoll && order[0].replacesAutoRollInstructionId
            ? { replacesAutoRollInstructionId: order[0].replacesAutoRollInstructionId }
            : {}),
        },
        idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined,
      );
      return response.data; // Returns the order ID
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Error submitting order:', error instanceof Error ? error.message : error);
      if (error?.response?.status === 409) {
        throw new Error('Request is already in progress');
      }
      throw error;
    }
  },

  getTreasuryBillDetail: async (id: string): Promise<TreasuryBillDetail> => {
    try {
      // Use the generated API method
      const response = await apiClient.api.assetsTreasuryBillsDetail(id);

      // Map the API response to domain type
      const mappedDetail = treasuryBillMapper.detailFromApi(response.data);

      return mappedDetail;
    } catch (error) {
      console.error(
        'Error fetching treasury bill detail:',
        error instanceof Error ? error.message : error,
      );
      throw new Error('Failed to fetch treasury bill detail');
    }
  },

  // `idempotencyKey` is a STABLE per-action key generated once per sell click
  // and reused across retries/double-submits so the server dedupes them.
  sellTreasuryBill: async (
    sellRequest: SellTreasuryBill,
    idempotencyKey?: string,
  ): Promise<string> => {
    try {
      // Map domain type to API command
      const command = treasuryBillMapper.sellRequestToCommand(sellRequest);

      // Use the generated API method
      const response = await apiClient.api.assetsTreasuryBillsSellCreate(
        command,
        idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined,
      );

      // Map response (currently just returns the order ID string)
      return treasuryBillMapper.sellResponseFromApi(response.data);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Error selling treasury bill:', error instanceof Error ? error.message : error);
      // 409 = duplicate / in-flight same key — static message, no auto-resubmit (#195).
      if (error?.response?.status === 409) {
        throw new Error('Request is already in progress');
      }
      // Otherwise parse the structured server-error body (ProblemDetails `code` OR
      // ValidationProblemDetails `errors`) into a typed SaleSubmitError so the
      // component branches cleanly. #206 / Task 2.5.
      throw parseSaleSubmitError(error, 'Failed to submit treasury bill sell order');
    }
  },

  // - getOrderHistory(businessId, filters?)
  // - cancelOrder(businessId, orderId)
  // - modifyOrder(businessId, orderId, updates)
  // - getAvailableInstruments()
};
